/* eslint-disable max-statements */
import moment from 'moment';

import { Declaration, ExitProofLogs, NlAesMessageType } from '@e-origin/shared';

const AAZ_TYPE = 'AAZ';
const SPH_TYPE = 'SPH';
const MAX_SPH_ROWS = 9;
const SPH_CODES_REQUIRING_TRANSPORT_DOCUMENTS = new Set(['11', '14', '15', '17']);
const AAZ_CODES_REQUIRING_EXIT_DATE = new Set(['2', '3', '4']);

type AdditionalInformationItem = NonNullable<ExitProofLogs['additionalInformation']>[number];
type TransportDocumentItem = NonNullable<ExitProofLogs['transportDocuments']>[number];

export interface ProofOfExitRules {
  hasCc582cRequest: boolean;
  aazCode: string;
  aazItem: AdditionalInformationItem;
  sphItems: AdditionalInformationItem[];
  allowedAazCodes: string[];
  canAddSph: boolean;
  enableExitOffice: boolean;
  requiresExitOffice: boolean;
  exitDateRelevant: boolean;
  enableCarrier: boolean;
  transportDocumentsRequired: boolean;
  requiresAtLeastOneSph: boolean;
  disableCarrierIdentificationNumber: boolean;
  disableCarrierNameAndAddress: boolean;
}

const isNonEmptyString = (value?: string | null) => !!value?.trim();

export const createEmptyCarrier = (): NonNullable<ExitProofLogs['carrier']> => ({
  name: '',
  identificationNumber: '',
  address: {
    city: '',
    country: '',
    street: '',
    postCode: '',
  },
});

export const createEmptyAazItem = (): AdditionalInformationItem => ({
  type: AAZ_TYPE,
  code: '',
});

export const createEmptySphItem = (): AdditionalInformationItem => ({
  type: SPH_TYPE,
  code: '',
  relation: { pointers: [] },
});

export const createEmptyTransportDocument = (): TransportDocumentItem => ({
  type: '',
  referenceNumber: '',
});

export const getHasCc582cRequest = (declaration?: Declaration): boolean =>
  declaration?.customsState?.customsLogs?.some((log) => log.customsMessageCode === NlAesMessageType.CC582C) ?? false;

export const getProofOfExitRules = (values: ExitProofLogs, declaration?: Declaration): ProofOfExitRules => {
  const additionalInformation = values.additionalInformation ?? [];

  const aazItem = additionalInformation.find((item) => item.type === AAZ_TYPE) ?? createEmptyAazItem();
  const sphItems = additionalInformation.filter((item) => item.type === SPH_TYPE);
  const aazCode = aazItem.code ?? '';
  const hasCc582cRequest = getHasCc582cRequest(declaration);
  const allowedAazCodes = hasCc582cRequest ? ['1', '2', '4'] : ['3', '4'];
  const hasSphRequiringTransportDocuments = sphItems.some((item) => SPH_CODES_REQUIRING_TRANSPORT_DOCUMENTS.has(item.code ?? ''));
  const requiresAtLeastOneSph = aazCode === '4';
  const exitDateRelevant = AAZ_CODES_REQUIRING_EXIT_DATE.has(aazCode) || hasSphRequiringTransportDocuments;
  const enableExitOffice = ['3', '4'].includes(aazCode);
  const requiresExitOffice = aazCode === '3';
  const enableCarrier = ['3', '4'].includes(aazCode);

  const carrier = values.carrier ?? createEmptyCarrier();
  const carrierAddress = carrier.address ?? createEmptyCarrier().address;
  const hasCarrierIdentificationNumber = isNonEmptyString(carrier.identificationNumber);
  const hasCarrierNameOrAddress = [carrier.name, carrierAddress.street, carrierAddress.postCode, carrierAddress.city, carrierAddress.country].some((value) =>
    isNonEmptyString(value),
  );

  return {
    hasCc582cRequest,
    aazCode,
    aazItem,
    sphItems,
    allowedAazCodes,
    canAddSph: sphItems.length < MAX_SPH_ROWS,
    enableExitOffice,
    requiresExitOffice,
    exitDateRelevant,
    enableCarrier,
    transportDocumentsRequired: hasSphRequiringTransportDocuments,
    requiresAtLeastOneSph,
    disableCarrierIdentificationNumber: hasCarrierNameOrAddress,
    disableCarrierNameAndAddress: hasCarrierIdentificationNumber,
  };
};

export const normalizeProofOfExitValues = (values: ExitProofLogs, declaration?: Declaration): ExitProofLogs => {
  const additionalInformation = values.additionalInformation ?? [];
  const aazCandidate = additionalInformation.find((item) => item.type === AAZ_TYPE) ?? createEmptyAazItem();
  const normalizedAaz: AdditionalInformationItem = {
    ...createEmptyAazItem(),
    ...aazCandidate,
    type: AAZ_TYPE,
  };

  const normalizedSphItems = additionalInformation
    .filter((item) => item.type === SPH_TYPE)
    .slice(0, MAX_SPH_ROWS)
    .map((item) => ({
      ...createEmptySphItem(),
      ...item,
      type: SPH_TYPE,
      relation: {
        pointers: item.relation?.pointers ?? [],
      },
    }));

  const nextValues: ExitProofLogs = {
    ...values,
    additionalInformation: [normalizedAaz, ...normalizedSphItems],
    transportDocuments: [...(values.transportDocuments ?? [])],
    carrier: {
      ...createEmptyCarrier(),
      ...(values.carrier ?? {}),
      address: {
        ...createEmptyCarrier().address,
        ...(values.carrier?.address ?? {}),
      },
    },
  };

  const rules = getProofOfExitRules(nextValues, declaration);

  if (!rules.enableExitOffice) {
    nextValues.exitOffice = '';
  }

  if (!rules.exitDateRelevant) {
    nextValues.exitDate = null;
  }

  if (!rules.transportDocumentsRequired) {
    nextValues.transportDocuments = [];
    nextValues.additionalInformation = (nextValues.additionalInformation ?? []).map((item) =>
      item.type === SPH_TYPE
        ? {
            ...item,
            relation: { pointers: [] },
          }
        : item,
    );
  } else {
    const validPointers = new Set((nextValues.transportDocuments ?? []).map((_, index) => index));
    nextValues.additionalInformation = (nextValues.additionalInformation ?? []).map((item) => {
      if (item.type !== SPH_TYPE) {
        return item;
      }

      const requiresPointer = SPH_CODES_REQUIRING_TRANSPORT_DOCUMENTS.has(item.code ?? '');
      const pointers = item.relation?.pointers ?? [];

      return {
        ...item,
        relation: {
          pointers: requiresPointer ? pointers.filter((pointer) => validPointers.has(pointer)) : [],
        },
      };
    });
  }

  if (!rules.enableCarrier) {
    nextValues.carrier = createEmptyCarrier();
  }

  return nextValues;
};

export const shouldSphRequireTransportDocuments = (code?: string) => SPH_CODES_REQUIRING_TRANSPORT_DOCUMENTS.has(code ?? '');

export const buildTransportDocumentPointerOptions = (transportDocuments?: ExitProofLogs['transportDocuments']) =>
  (transportDocuments ?? []).map((document, index) => ({
    value: `${index}`,
    label: [document.type, document.referenceNumber].filter(isNonEmptyString).join(' / ') || `Transport document ${index + 1}`,
  }));

export const validateProofOfExit = (values: ExitProofLogs, declaration?: Declaration) => {
  const errors: Record<string, string> = {};
  const rules = getProofOfExitRules(values, declaration);
  const additionalInformation = values.additionalInformation ?? [];
  const transportDocuments = values.transportDocuments ?? [];
  const aazItems = additionalInformation.filter((item) => item.type === AAZ_TYPE);
  const sphItems = additionalInformation.filter((item) => item.type === SPH_TYPE);
  const exitDate = values.exitDate ? moment(values.exitDate) : null;
  const today = moment().startOf('day');

  if (aazItems.length !== 1) {
    errors.additionalInformation = 'Exactly one AAZ statement is required.';
  }

  if (sphItems.length > MAX_SPH_ROWS) {
    errors.additionalInformation = `A maximum of ${MAX_SPH_ROWS} SPH statements is allowed.`;
  }

  if (!rules.allowedAazCodes.includes(rules.aazCode)) {
    if (!rules.aazCode) {
      errors['additionalInformation.0.code'] = 'AAZ code is required.';
    } else {
      errors['additionalInformation.0.code'] = 'This AAZ code is not allowed for the current customs-request state.';
    }
  }

  if (rules.requiresAtLeastOneSph && sphItems.length === 0) {
    errors.additionalInformation = 'At least one SPH statement is required when AAZ code 4 is used.';
  }

  if (rules.requiresExitOffice && !isNonEmptyString(values.exitOffice)) {
    errors.exitOffice = 'Exit office is required.';
  }

  if (rules.exitDateRelevant && !exitDate) {
    errors.exitDate = 'Exit date is required.';
  }

  if (exitDate && !exitDate.isValid()) {
    errors.exitDate = 'Exit date is invalid.';
  }

  if (exitDate?.isValid() && rules.aazCode === '2' && !exitDate.clone().startOf('day').isAfter(today)) {
    errors.exitDate = 'Code 2 requires a future/expected exit date.';
  }

  if (exitDate?.isValid() && (rules.aazCode === '3' || rules.aazCode === '4') && exitDate.clone().startOf('day').isAfter(today)) {
    errors.exitDate = `Code ${rules.aazCode} requires an exit date that is today or earlier.`;
  }

  if (rules.transportDocumentsRequired && transportDocuments.length === 0) {
    errors.transportDocuments = 'At least one transport document is required.';
  }

  if (!rules.transportDocumentsRequired && transportDocuments.length > 0) {
    errors.transportDocuments = 'Transport documents are not allowed for the current statement selection.';
  }

  const carrier = values.carrier ?? createEmptyCarrier();
  const carrierAddress = carrier.address ?? createEmptyCarrier().address;
  const hasCarrierIdentificationNumber = isNonEmptyString(carrier.identificationNumber);
  const hasCarrierNameOrAddress = [carrier.name, carrierAddress.street, carrierAddress.postCode, carrierAddress.city, carrierAddress.country].some((value) =>
    isNonEmptyString(value),
  );

  if (hasCarrierIdentificationNumber && hasCarrierNameOrAddress) {
    errors.carrier = 'Carrier identification number cannot be sent together with carrier name/address.';
  }

  const validPointers = new Set(transportDocuments.map((_, index) => index));

  additionalInformation.forEach((item, index) => {
    if (item.type !== SPH_TYPE) {
      return;
    }

    if (shouldSphRequireTransportDocuments(item.code) && (item.relation?.pointers?.length ?? 0) === 0) {
      errors[`additionalInformation.${index}.relation.pointers`] = 'At least one related transport document is required for this SPH code.';
    }

    if ((item.relation?.pointers ?? []).some((pointer) => !validPointers.has(pointer))) {
      errors[`additionalInformation.${index}.relation.pointers`] = 'One or more selected transport documents are no longer available.';
    }
  });

  return errors;
};

export const hasMeaningfulProofOfExitValue = (value: unknown, path = ''): boolean => {
  if (Array.isArray(value)) {
    return value.some((item, index) => hasMeaningfulProofOfExitValue(item, `${path}.${index}`));
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).some(([key, nestedValue]) => {
      if (path.includes('additionalInformation') && key === 'type') {
        return false;
      }

      return hasMeaningfulProofOfExitValue(nestedValue, path ? `${path}.${key}` : key);
    });
  }

  return typeof value === 'string' ? value.trim().length > 0 : value !== null && value !== undefined;
};
